import * as Api from '@ViewModels';
import { Tooltip, TooltipContent, TooltipTrigger } from '@WebComponents/Tooltip';
import { css } from 'aphrodite';
import * as React from 'react';
import { useHistory, useLocation } from 'react-router';
import { toast } from 'sonner';
import { IContactSelection, ICreateCampaignRequest, ILocationState } from '../../../models';
import { EventLogger } from '../../../models/Logging';
import { getDisplayName } from '../../../models/NameUtils';
import { getMentionEntitiesFromLocationState } from '../../../models/UiUtils';
import {
	useActionItemComposer,
	useFullscreenModal,
	useNoteComposer,
	useQuickAddEntity,
	useSingleEmailComposer,
	useUserSession,
} from '../../../models/hooks/appStateHooks';
import { invalidateContactTimeline } from '../../../queries/Contact/useInfiniteContactTimeline';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '../../components/HoverCard';
import { ActionItemsIcon } from '../../components/svgs/icons/ActionItemsIcon';
import { CallIcon } from '../../components/svgs/icons/CallIcon';
import { EmailIcon } from '../../components/svgs/icons/EmailIcon';
import { NewContactIcon } from '../../components/svgs/icons/NewContactIcon';
import { SvgIcon } from '../../components/svgs/icons/SvgIcon';
import { THEME } from '../Theme/theme';
import { styleSheet } from './styles';

/** FAB button integrated into AppDrawer toolbar */
export const AppDrawerFab = ({ extendedFabButtons }: { extendedFabButtons?: React.ReactNode }) => {
	const location = useLocation();
	const history = useHistory();
	const userSession = useUserSession();
	const noteComposer = useNoteComposer();
	const actionItemComposer = useActionItemComposer();
	const singleEmailComposer = useSingleEmailComposer();

	// These hooks might not be available in tests, but we need to call them unconditionally
	// We'll handle errors in the usage instead
	const fullscreenModal = useFullscreenModal();
	const quickAddEntity = useQuickAddEntity();

	const [isMainButtonHovering, setIsMainButtonHovering] = React.useState(false);

	const onNoteCreated = (note?: Api.NoteViewModel, result?: Api.RichContentComposerResult) => {
		if (!!note && result !== Api.RichContentComposerResult.Delete && result !== Api.RichContentComposerResult.None) {
			// Invalidate timeline queries for all referenced contacts
			const noteModel = note.toNote();
			const referencedContacts = noteModel.referencedEntities?.contacts || [];
			referencedContacts.forEach((contactRef: any) => {
				if (contactRef.entity?.id) {
					invalidateContactTimeline({ contactId: contactRef.entity.id });
				}
			});
		}
	};

	const onStartComposingNote = (context?: Api.IRichContentContext) => {
		const locationState: ILocationState<Api.EntityViewModel<Api.IEntity> | Api.OpportunityViewModel, Api.IEntity> =
			fullscreenModal.hasValidLocation ? fullscreenModal.history.location.state : location.state;
		const entities = getMentionEntitiesFromLocationState(locationState);
		const note: Api.INote = Api.createRichContentWithReferencedEntities(entities || []);
		note.context = context || null;

		// add "Phone call with..." to content, if needed
		if (
			!!note.context &&
			note.context.source === Api.RichContentContextSource.PhoneCall &&
			!!entities &&
			entities.length > 0
		) {
			const entityListStringValue = entities.reduce<string>((result, entityModel, i) => {
				const name = (getDisplayName(entityModel) || '').trim();
				if (name) {
					return `${result}${result ? (i === entities.length - 1 ? ' and ' : ', ') : ''}${name}`;
				}
				return result;
			}, '');
			if (entityListStringValue) {
				note.content = Api.createRawRichTextContentStateWithText(`Phone call with ${entityListStringValue}.`);
			}
		}
		EventLogger.logInput('Fab', 'CreateNote', 'Click');
		const noteVm = new Api.NoteViewModel(userSession, note);
		noteComposer.show(noteVm, onNoteCreated);
	};
	const onEditActionItemCompleted = (actionItem?: Api.ActionItemViewModel, result?: Api.RichContentComposerResult) => {
		if (
			!!actionItem &&
			result !== Api.RichContentComposerResult.Delete &&
			result !== Api.RichContentComposerResult.None
		) {
			// Invalidate timeline queries for all referenced contacts
			const actionItemModel = actionItem.toActionItem();
			const referencedContacts = actionItemModel.referencedEntities?.contacts || [];
			referencedContacts.forEach((contactRef: any) => {
				if (contactRef.entity?.id) {
					invalidateContactTimeline({ contactId: contactRef.entity.id });
				}
			});

			// action item was created... toast
			toast.success('Your action item was saved.', {
				action: {
					label: 'See Action Item',
					onClick: () => history.push(`/notes-action-items?activeTab=action-items`),
				},
			});
		}
	};
	const onSendEmail = () => {
		// try to get the contact vm from the current location state.
		const locationState: ILocationState<Api.ContactViewModel, Api.IEntity> = fullscreenModal.hasValidLocation
			? fullscreenModal.history.location.state
			: location.state;
		let contacts: Api.ContactViewModel[] = (getMentionEntitiesFromLocationState(locationState) || [])
			.filter(x => Api.getTypeForEntity(x) !== 'company')
			.map(x => new Api.ContactViewModel(userSession, x));
		if (
			(!contacts || contacts.length === 0) &&
			!!locationState &&
			!!locationState.model &&
			Api.getTypeForEntity(locationState.model) !== 'company'
		) {
			contacts = [new Api.ContactViewModel(userSession, locationState.model)];
		}
		EventLogger.logInput(
			'Fab',
			'EmailComposer',
			'Click',
			contacts
				? {
						contacts: contacts.map(x => {
							return { id: x.id };
						}),
					}
				: undefined
		);
		if (!!contacts && contacts.length > 0) {
			singleEmailComposer.showForRecipients(contacts);
		} else {
			const selectionLocationState: ILocationState<any, ICreateCampaignRequest<IContactSelection>> = {
				model: {
					context: {
						contacts: contacts || [],
					},
					type: 'Contacts',
				},
			};
			fullscreenModal?.history?.push({
				pathname: '/email/campaigns/create/from-scratch',
				state: selectionLocationState,
			});
		}
	};
	const onAddActionItem = () => {
		EventLogger.logInput('Fab', 'CreateActionItem', 'Click');
		const locationState: ILocationState<Api.EntityViewModel<Api.IEntity> | Api.OpportunityViewModel, Api.IEntity> =
			fullscreenModal.hasValidLocation ? fullscreenModal.history.location.state : location.state;
		const entities = getMentionEntitiesFromLocationState(locationState);
		const actionItemModel: Api.IActionItem = Api.createRichContentWithReferencedEntities(entities || []);
		const actionItem = new Api.ActionItemViewModel(userSession, actionItemModel);
		actionItemComposer.show(actionItem, onEditActionItemCompleted);
	};
	const onAddPhoneCall = () => {
		EventLogger.logInput('Fab', 'AddPhoneCall', 'Click');
		onStartComposingNote({
			source: Api.RichContentContextSource.PhoneCall,
		});
	};
	const onNewContact = () => {
		if (quickAddEntity && quickAddEntity.show) {
			quickAddEntity.show({ type: 'contact' });
		}
	};

	return (
		<HoverCard openDelay={0} open={isMainButtonHovering} onOpenChange={setIsMainButtonHovering}>
			<Tooltip>
				<TooltipTrigger asChild>
					<HoverCardTrigger
						className={css(styleSheet.drawerButton)}
						onClick={() => onStartComposingNote()}
						data-testid='app-drawer-fab-button'
					>
						<SvgIcon height={22} width={22} style={{ pointerEvents: 'none' }}>
							<g fill='none' fillRule='evenodd'>
								{isMainButtonHovering ? (
									// Edit icon when menu is open
									<g stroke='currentColor' strokeWidth='2' transform='translate(3 1)'>
										<polygon points='9.032 0 12.316 3.537 3.284 13.263 0 13.263 0 9.726' />
										<path d='M-1.15463195e-14,17.0526316 L15.1578947,17.0526316' />
									</g>
								) : (
									// Plus icon when menu is closed
									<path
										d='M8.7158302,0 C8.1626925,0 7.7142857,0.4480965 7.7142857,1.0085081 L7.7142857,7.7142857 L1.0085081,7.7142857 C0.4515245,7.7142857 -3.55271368e-15,8.1621944 -3.55271368e-15,8.7158302 L-3.55271368e-15,9.2841698 C-3.55271368e-15,9.8373075 0.4480965,10.2857143 1.0085081,10.2857143 L7.7142857,10.2857143 L7.7142857,16.9914919 C7.7142857,17.5484755 8.1621944,18 8.7158302,18 L9.2841698,18 C9.8373075,18 10.2857143,17.5519035 10.2857143,16.9914919 L10.2857143,10.2857143 L16.9914919,10.2857143 C17.5484755,10.2857143 18,9.8378056 18,9.2841698 L18,8.7158302 C18,8.1626925 17.5519035,7.7142857 16.9914919,7.7142857 L10.2857143,7.7142857 L10.2857143,1.0085081 C10.2857143,0.4515245 9.8378056,0 9.2841698,0 L8.7158302,0 Z'
										fill='currentColor'
										transform='translate(1 2)'
									/>
								)}
								<rect width='22' height='22' />
							</g>
						</SvgIcon>
					</HoverCardTrigger>
				</TooltipTrigger>
				<TooltipContent side='left'>Add note</TooltipContent>
			</Tooltip>
			<HoverCardContent side='top' styles={[styleSheet.fabMenu]} sideOffset={8}>
				{extendedFabButtons}
				<AppDrawerFabButton
					tooltipText='Send an email'
					icon={<EmailIcon fillColor={THEME.colors.icon.primary} />}
					onClick={onSendEmail}
				/>
				<AppDrawerFabButton
					tooltipText='Add Action Item'
					icon={<ActionItemsIcon fillColor={THEME.colors.icon.primary} />}
					onClick={onAddActionItem}
				/>
				<AppDrawerFabButton
					tooltipText='Add Phone Call'
					icon={<CallIcon fillColor={THEME.colors.icon.primary} />}
					onClick={onAddPhoneCall}
				/>
				<AppDrawerFabButton
					tooltipText='New Contact'
					icon={<NewContactIcon fillColor={THEME.colors.icon.primary} />}
					onClick={onNewContact}
				/>
			</HoverCardContent>
		</HoverCard>
	);
};

export function AppDrawerFabButton({
	tooltipText,
	icon,
	onClick,
}: {
	tooltipText: string;
	icon: React.ReactNode;
	onClick: () => void;
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<button title={tooltipText} className={css(styleSheet.fabMenuItemAppDrawer)} onClick={onClick}>
					{icon}
				</button>
			</TooltipTrigger>
			<TooltipContent side='left'>{tooltipText}</TooltipContent>
		</Tooltip>
	);
}
